/* =============================================================================
 *  andreiTasksApi — client calls for the Andrei Autonomous Admin engine's
 *  management surface (Phase 1). Tasks are CREATED conversationally (ask Andrei:
 *  "remind me on Friday to…", "chase Acme if no reply by next week") via the
 *  scheduleTask tool, or land here to be listed / paused / cancelled / run-now.
 *
 *  All mutations go through the owner-gated manageAndreiTasks / runAndreiTaskNow
 *  endpoints (firestore.rules forbids client writes to andrei_tasks). A non-owner
 *  calling these gets a clean 403.
 * ============================================================================= */

import { authFetch } from '../../utils/cloudFunctionsAuth';

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

export interface AndreiTaskRow {
  id: string;
  title: string;
  mode: 'notify' | 'act';
  status: 'scheduled' | 'running' | 'done' | 'cancelled' | 'error' | 'paused' | 'expired';
  triggerType: string;
  trigger: string;            // human-readable description
  instruction: string | null;
  customerId: string | null;
  nextRunAt: string | null;   // ISO
  lastRunAt: string | null;   // ISO
  lastOutcome: string | null;
  runCount: number;
  lastError?: string | null;
  sessionId?: string | null;
  supportReview?: {
    planId: string; month: string; issueId: string; episode: number;
    kind: 'outstanding' | 'overdue' | 'integrity' | 'generation_error'; jobId: string | null;
  } | null;
}

async function post<T>(path: string, body: Record<string, any>): Promise<T> {
  const r = await authFetch(`${CF_BASE}/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  const text = await r.text();
  let data: any = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { ok: false, error: text || 'bad response' }; }
  if (!r.ok && data.ok === undefined) data = { ok: false, error: data.error || `HTTP ${r.status}` };
  return data as T;
}

/** List the owner's scheduled tasks (owner-gated). */
export function listAndreiTasks(includeInactive = false): Promise<{ ok: boolean; tasks?: AndreiTaskRow[]; error?: string }> {
  return post('manageAndreiTasks', { action: 'list', includeInactive });
}

/** Cancel a task (owner-gated). */
export function cancelAndreiTask(id: string): Promise<{ ok: boolean; status?: string; error?: string }> {
  return post('manageAndreiTasks', { action: 'cancel', id });
}

/** Pause / resume a task (owner-gated). */
export function setAndreiTaskPaused(id: string, paused: boolean): Promise<{ ok: boolean; status?: string; error?: string }> {
  return post('manageAndreiTasks', { action: paused ? 'pause' : 'resume', id });
}

/** Delete a task permanently (owner-gated). */
export function deleteAndreiTask(id: string): Promise<{ ok: boolean; deleted?: boolean; error?: string }> {
  return post('manageAndreiTasks', { action: 'delete', id });
}

/** Run one task immediately (owner-gated). */
export function runAndreiTaskNow(taskId: string): Promise<{ ok: boolean; outcome?: string; error?: string }> {
  return post('runAndreiTaskNow', { taskId });
}
